'use client'

import React, { useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useRouter } from 'next/navigation'
import { useDocumentationContext } from './documentation-provider'
import { parseTrainingGuide } from '@/utils/documentation-parser'
import { useDocumentationContent } from '@/hooks/useDocumentationContent'
import { useDynamicDocumentationContent } from '@/hooks/useDynamicDocumentationContent'
import { addDocumentationLinks, isInternalLink } from '@/utils/documentation-links'
import { cn } from '@/lib/utils'

export function DocumentationContent() {
  const { currentSection, currentArticle, close } = useDocumentationContext()
  const { content: originalContent, loading } = useDocumentationContent()
  const router = useRouter()
  
  // Apply dynamic content replacements for logged-in users
  const content = useDynamicDocumentationContent(originalContent)
  
  // Parse the training guide into sections
  const sections = useMemo(() => {
    if (!content) return []
    return parseTrainingGuide(content)
  }, [content])

  // Find the current article content and add hyperlinks
  const currentContent = useMemo(() => {
    if (!currentSection || !currentArticle) return null
    
    const section = sections.find(s => s.id === currentSection)
    if (!section) return null
    
    const article = section.articles.find(a => a.id === currentArticle)
    if (!article?.content) return null
    
    // Add hyperlinks to the content
    return addDocumentationLinks(article.content)
  }, [sections, currentSection, currentArticle])

  if (loading) {
    return (
      <article className="flex-1 overflow-y-auto bg-white p-8">
        <div className="space-y-4">
          {/* Loading skeleton */}
          <div className="h-8 bg-gray-200 rounded w-1/3 animate-pulse" />
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded w-full animate-pulse" />
            <div className="h-4 bg-gray-200 rounded w-5/6 animate-pulse" />
            <div className="h-4 bg-gray-200 rounded w-4/5 animate-pulse" />
          </div>
        </div>
      </article>
    )
  }

  if (!currentContent) {
    return (
      <div className="flex-1 p-8 flex items-center justify-center text-gray-500">
        <div className="text-center">
          <p className="text-lg font-medium mb-2">Welcome to the User Guide</p>
          <p className="text-sm">Select a topic from the left to get started</p>
        </div>
      </div>
    )
  }

  return (
    <article className="flex-1 overflow-y-auto bg-white">
      <div className="prose prose-sm max-w-none p-6">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            // Customize markdown rendering
            h1: ({ children }) => (
              <h1 className="text-2xl font-bold text-gray-900 mb-4 border-b pb-2">
                {children}
              </h1>
            ),
            h2: ({ children }) => (
              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                {children}
              </h2>
            ),
            h3: ({ children }) => (
              <h3 className="text-lg font-medium text-gray-900 mt-5 mb-2">
                {children}
              </h3>
            ),
            h4: ({ children }) => (
              <h4 className="text-base font-medium text-gray-800 mt-4 mb-2">
                {children}
              </h4>
            ),
            p: ({ children }) => (
              <p className="text-gray-700 leading-relaxed mb-3">
                {children}
              </p>
            ),
            ul: ({ children }) => (
              <ul className="list-disc list-inside space-y-1 mb-3 text-gray-700">
                {children}
              </ul>
            ),
            ol: ({ children }) => (
              <ol className="list-decimal list-inside space-y-1 mb-3 text-gray-700">
                {children}
              </ol>
            ),
            li: ({ children }) => (
              <li className="leading-relaxed">
                {children}
              </li>
            ),
            strong: ({ children }) => (
              <strong className="font-semibold text-gray-900">
                {children}
              </strong>
            ),
            em: ({ children }) => (
              <em className="italic text-gray-700">
                {children}
              </em>
            ),
            code: ({ children }) => (
              <code className={cn(
                "px-1.5 py-0.5 rounded",
                "bg-purple-50 text-purple-700",
                "font-mono text-xs"
              )}>
                {children}
              </code>
            ),
            pre: ({ children }) => (
              <pre className={cn(
                "p-4 rounded-lg overflow-x-auto",
                "bg-gray-50 border border-gray-200",
                "text-sm mb-3"
              )}>
                {children}
              </pre>
            ),
            blockquote: ({ children }) => (
              <blockquote className={cn(
                "pl-4 border-l-4 border-purple-300",
                "text-gray-700 italic my-3"
              )}>
                {children}
              </blockquote>
            ),
            hr: () => (
              <hr className="my-6 border-gray-200" />
            ),
            a: ({ href, children }) => {
              // Check if it's an internal link
              if (href && isInternalLink(href)) {
                return (
                  <a
                    href={href}
                    onClick={(e) => {
                      e.preventDefault()
                      close() // Close the documentation panel
                      router.push(href) // Navigate to the page
                    }}
                    className="text-purple-600 hover:text-purple-700 underline cursor-pointer"
                  >
                    {children}
                  </a>
                )
              }
              
              // External link
              return (
                <a
                  href={href}
                  className="text-purple-600 hover:text-purple-700 underline"
                  target={href?.startsWith('http') ? '_blank' : undefined}
                  rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                >
                  {children}
                </a>
              )
            },
            table: ({ children }) => (
              <div className="overflow-x-auto mb-4">
                <table className="min-w-full divide-y divide-gray-200">
                  {children}
                </table>
              </div>
            ),
            thead: ({ children }) => (
              <thead className="bg-gray-50">
                {children}
              </thead>
            ),
            tbody: ({ children }) => (
              <tbody className="bg-white divide-y divide-gray-200">
                {children}
              </tbody>
            ),
            tr: ({ children }) => (
              <tr>
                {children}
              </tr>
            ),
            th: ({ children }) => (
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {children}
              </th>
            ),
            td: ({ children }) => (
              <td className="px-3 py-2 text-sm text-gray-700">
                {children}
              </td>
            ),
          }}
        >
          {currentContent}
        </ReactMarkdown>
      </div>
    </article>
  )
}